import ApplicationController from "./application_controller";
import { validate } from "email-validator"

// Häufige E-Mail-Domains (international, DE, AT) für den "Meinten Sie …?"-Vorschlag.
// Dient nur als Tippfehler-Hilfe an der Eingabe – es wird nichts blockiert.
const KNOWN_DOMAINS = [
  "gmail.com", "googlemail.com", "yahoo.com", "yahoo.de", "yahoo.at",
  "hotmail.com", "hotmail.de", "hotmail.at", "outlook.com", "outlook.de",
  "live.com", "live.de", "icloud.com", "me.com", "aol.com",
  "proton.me", "protonmail.com", "gmx.at", "gmx.net", "gmx.de", "gmx.com",
  "web.de", "t-online.de", "freenet.de", "aon.at", "a1.net", "chello.at",
  "kabsi.at", "utanet.at", "liwest.at", "inode.at", "drei.at"
]

// Levenshtein-Distanz (Editierdistanz) zwischen zwei Strings.
function levenshtein(a, b) {
  const m = a.length, n = b.length
  if (m === 0) return n
  if (n === 0) return m
  let prev = Array.from({ length: n + 1 }, (_, i) => i)
  for (let i = 1; i <= m; i++) {
    const cur = [i]
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost)
    }
    prev = cur
  }
  return prev[n]
}

// Liefert eine korrigierte Domain, wenn die eingegebene nahe an einer bekannten
// Domain liegt (Editierdistanz 1–2, z.B. gmail.con -> gmail.com), sonst null.
export function suggestDomain(domain) {
  const d = (domain || "").toLowerCase()
  if (d === "" || KNOWN_DOMAINS.includes(d)) return null
  let best = null, bestDist = Infinity
  for (const cand of KNOWN_DOMAINS) {
    const dist = levenshtein(d, cand)
    if (dist < bestDist) { bestDist = dist; best = cand }
  }
  return bestDist > 0 && bestDist <= 2 ? best : null
}

export default class extends ApplicationController {
  static targets = ["email"]

  disconnect() {
    if (this._hint) { this._hint.remove(); this._hint = null }
  }

  validate() {
    const value = this.emailTarget.value.trim()
    if (value === "" || validate(value)) {
      this.emailTarget.setCustomValidity("")
    } else {
      this.emailTarget.setCustomValidity("Invalid email address")
    }
    this.updateSuggestion(value)
  }

  updateSuggestion(value) {
    const at = value.lastIndexOf("@")
    if (at < 1 || at === value.length - 1) { this.clearHint(); return }

    const local = value.slice(0, at)
    const domain = value.slice(at + 1)
    const suggestion = suggestDomain(domain)

    if (suggestion) {
      this.showHint(`${local}@${suggestion}`)
    } else {
      this.clearHint()
    }
  }

  showHint(suggestedEmail) {
    const hint = this.hintElement()
    hint.textContent = ""

    hint.appendChild(document.createTextNode("Meinten Sie "))

    const link = document.createElement("a")
    link.href = "#"
    link.className = "underline font-medium"
    link.textContent = suggestedEmail
    link.addEventListener("click", (event) => {
      event.preventDefault()
      this.emailTarget.value = suggestedEmail
      this.validate()
      this.emailTarget.focus()
    })
    hint.appendChild(link)
    hint.appendChild(document.createTextNode("?"))

    hint.classList.remove("hidden")
  }

  clearHint() {
    if (this._hint) {
      this._hint.textContent = ""
      this._hint.classList.add("hidden")
    }
  }

  hintElement() {
    // Vorhandenes Hinweis-Element wiederverwenden. Ohne das fügte ein zweites
    // connect() (z.B. nach Turbo-Cache-Restore, bei dem this._hint in der neuen
    // Instanz leer ist) ein weiteres Element ein -> der Hinweis erschien doppelt.
    const sibling = this.emailTarget.nextElementSibling
    if (sibling && sibling.hasAttribute("data-email-suggestion")) {
      this._hint = sibling
    }
    if (!this._hint) {
      this._hint = document.createElement("div")
      this._hint.setAttribute("data-email-suggestion", "")
      this._hint.className = "mt-1 text-sm text-yellow-700"
      this._hint.setAttribute("aria-live", "polite")
      this.emailTarget.insertAdjacentElement("afterend", this._hint)
    }
    return this._hint
  }
}
